import { useState } from "react"
import { Plus, Pencil, Leaf } from "lucide-react"

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"

function initials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Chip summarizing the current saved search (resume + taxonomies + locations)
function SavedFilterChip({ label, onEdit }) {
  const [hov, setHov] = useState(false)
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 10px 10px 14px",
        background: hov ? "#f8f9fa" : "transparent",
        borderRadius: 8,
        transition: "background 0.12s ease",
        cursor: "default",
      }}
    >
      {/* Left green accent bar */}
      <span style={{
        position: "absolute", left: 0, top: 10, bottom: 10,
        width: 2, borderRadius: 2,
        background: "#00f0a0",
      }} />
      <span style={{
        flex: 1, minWidth: 0,
        fontSize: 13, fontWeight: 500, color: "#18181b", fontFamily: FF,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {label}
      </span>
      <button
        onClick={onEdit}
        aria-label="Edit saved filter"
        style={{
          width: 28, height: 28, borderRadius: 6,
          background: "transparent", border: "none", cursor: "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: "#86909c",
          transition: "background 0.12s ease, color 0.12s ease",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#f3f4f5"; e.currentTarget.style.color = "#18181b" }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#86909c" }}
      >
        <Pencil size={14} strokeWidth={2} />
      </button>
    </div>
  )
}

export default function DashboardRightPanel({
  user,
  userData,
  resumes = [],
  selectedResumeId,
  selectedTaxonomies = [],
  selectedLocations = [],
  selectedLevels = [],
  plan = "Free Plan",
  onAddFilter,
  onEditFilter,
}) {
  const name = (userData?.name || user?.displayName || user?.email?.split("@")[0] || "You").toString()
  const nameLabel = name.toUpperCase()
  const avatar = initials(name)

  // Compose a short saved-filter summary from the active filters
  const selectedResume = resumes.find(r => r.id === selectedResumeId)
  const filterParts = []
  if (selectedResume) filterParts.push((selectedResume.fileName || selectedResume.name || "Resume").replace(/\.pdf$/i, ""))
  if (selectedTaxonomies.length > 0) {
    const titles = selectedTaxonomies.slice(0, 2).map(t => t.title).filter(Boolean)
    if (titles.length) filterParts.push(titles.join(", ") + (selectedTaxonomies.length > 2 ? ` +${selectedTaxonomies.length - 2}` : ""))
  }
  if (selectedLocations.length > 0) {
    const locs = selectedLocations.slice(0, 2).join(", ")
    filterParts.push(locs + (selectedLocations.length > 2 ? ` +${selectedLocations.length - 2}` : ""))
  }
  if (selectedLevels.length > 0) filterParts.push(selectedLevels[0])
  const filterLabel = filterParts.length > 0 ? filterParts.join(" · ") : null

  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 20,
      padding: "20px 16px",
      fontFamily: FF,
    }}>
      {/* ── User header ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "#f3f4f5",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, fontWeight: 700, color: "#18181b", fontFamily: FF,
          flexShrink: 0,
        }}>{avatar}</div>
        <span style={{
          flex: 1, minWidth: 0,
          fontSize: 14, fontWeight: 700, color: "#18181b", fontFamily: FF,
          letterSpacing: "0.02em",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{nameLabel}</span>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "4px 10px", borderRadius: 9999,
          background: "rgba(0, 240, 160, 0.14)",
          fontSize: 12, fontWeight: 600, color: "#065f46", fontFamily: FF,
          flexShrink: 0,
        }}>
          <Leaf size={12} strokeWidth={2.2} />
          {plan}
        </span>
      </div>

      {/* ── Your Saved Filters ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#18181b", fontFamily: FF }}>
            Your Saved Filters
          </span>
          <button
            onClick={onAddFilter}
            aria-label="Add saved filter"
            style={{
              width: 28, height: 28, borderRadius: "50%",
              background: "#18181b", color: "#ffffff", border: "none",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
              transition: "background 0.15s ease",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#27272a" }}
            onMouseLeave={e => { e.currentTarget.style.background = "#18181b" }}
          >
            <Plus size={14} strokeWidth={2.4} />
          </button>
        </div>

        {filterLabel ? (
          <SavedFilterChip label={filterLabel} onEdit={onEditFilter} />
        ) : (
          <div style={{
            padding: "12px 14px",
            fontSize: 12.5, color: "#86909c", fontFamily: FF,
            borderRadius: 8, background: "#f8f9fa",
          }}>
            No saved filters yet. Tap <strong>+</strong> to save your current search.
          </div>
        )}
      </div>
    </div>
  )
}
